import { GridLegacy as Grid, Typography, Button } from '@mui/material';
import { Alert } from '@mui/material';
import { useFormHook } from '@monorepo/react/hook-form-helper';
import { CustomTextField } from '@monorepo/react/material-form-inputs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoginProps } from '..';
import { joinPath } from './path.utils';

const MIN_PASSWORD_LENGTH = 16;
const MAX_PASSWORD_LENGTH = 128;
const SPECIAL_CHARACTER_REGEX = /(?=.*[@!"'€µ{}|–§~²³$%&/()=?+*#-.:,;<>])/i;

type ResetPasswordProps = Pick<
  LoginProps,
  'resetPasswordMutation' | 'validateToken' | 'pathPrefix'
>;

// eslint-disable-next-line max-lines-per-function
export const ResetPassword: React.FC<ResetPasswordProps> = ({
  resetPasswordMutation,
  validateToken,
  pathPrefix = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ token: string }>();
  const rawToken = React.useMemo(() => {
    const basePath = `${joinPath(pathPrefix, 'resetPassword')}/`;
    const pathname = location.pathname || '';

    if (pathname.startsWith(basePath)) {
      return pathname.slice(basePath.length);
    }

    if (params?.token) {
      return params.token;
    }

    const searchParams = new URLSearchParams(location.search);
    const tokenFromQuery = searchParams.get('token');
    if (tokenFromQuery) {
      return tokenFromQuery;
    }

    return '';
  }, [location.pathname, location.search, params?.token, pathPrefix]);
  const token = React.useMemo(() => {
    if (!rawToken) return '';
    try {
      return decodeURIComponent(rawToken).replace(/ /g, '+');
    } catch {
      return rawToken.replace(/ /g, '+');
    }
  }, [rawToken]);
  const validateTokenRef = React.useRef(validateToken);
  const lastValidatedTokenRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    validateTokenRef.current = validateToken;
  }, [validateToken]);

  const {
    register,
    handleSubmit,
    errors,
    watch,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = useFormHook({
    defaultValues: { password: '', passwordConfirmation: '', token },
    onSubmit: async (formData) => {
      const result = await resetPasswordMutation({
        variables: { ...formData, token },
      });
      if (result.errors) return { errors: result.errors };
      if (!result.data?.resetPassword) return {};
      localStorage.removeItem('tokenInvalid');
      localStorage.setItem('passwordResetSuccess', 'true');
      navigate(joinPath(pathPrefix));
      return {};
    },
  });

  React.useEffect(() => {
    if (isSubmitSuccessful) return;
    if (lastValidatedTokenRef.current === token) return;

    let cancelled = false;

    const fetchTokenValidation = async () => {
      try {
        if (!rawToken) {
          localStorage.setItem('tokenInvalid', 'true');
          navigate(joinPath(pathPrefix));
          return;
        }

        if (!token) {
          localStorage.setItem('tokenInvalid', 'true');
          navigate(joinPath(pathPrefix));
          return;
        }

        lastValidatedTokenRef.current = token;

        const result = await validateTokenRef.current({
          variables: { token },
        });

        if (cancelled) return;

        if (result.errors || !result.data?.validateToken) {
          localStorage.setItem('tokenInvalid', 'true');
          navigate(joinPath(pathPrefix));
        }
      } catch {
        if (cancelled) return;
        localStorage.setItem('tokenInvalid', 'true');
        navigate(joinPath(pathPrefix));
      }
    };

    fetchTokenValidation();

    return () => {
      cancelled = true;
    };
  }, [token, rawToken, navigate, pathPrefix, isSubmitSuccessful]);

  const passwordValue = watch('password');
  const passwordErrorMessage = String(t('login.passwordReset.helperText'));

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {String(t('login.passwordReset.self'))}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {String(t('login.passwordReset.helperText'))}
        </Typography>
      </Grid>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <input
          key={token}
          hidden
          style={{ display: 'none' }}
          defaultValue={token}
          {...register('token')}
        />
        <Grid container spacing={2}>
          {errors.token && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {String(t('login.errors.token'))}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <CustomTextField
              name="password"
              type="password"
              errors={errors}
              helperText=""
              label={String(t('login.password'))}
              {...register('password', {
                required: true,
                validate: (value) => {
                  if (typeof value !== 'string') return passwordErrorMessage;
                  if (value.length < MIN_PASSWORD_LENGTH)
                    return passwordErrorMessage;
                  if (value.length > MAX_PASSWORD_LENGTH)
                    return passwordErrorMessage;
                  if (!/[a-z]/.test(value)) return passwordErrorMessage;
                  if (!/[A-Z]/.test(value)) return passwordErrorMessage;
                  if (!/\d/.test(value)) return passwordErrorMessage;
                  if (!SPECIAL_CHARACTER_REGEX.test(value))
                    return passwordErrorMessage;
                  return true;
                },
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              name="passwordConfirmation"
              type="password"
              errors={errors}
              label={String(t('login.passwordConfirmation'))}
              {...register('passwordConfirmation', {
                required: true,
                validate: (value) =>
                  value === passwordValue ||
                  String(t('global.forms.validationErrors.Match')),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isDirty}
            >
              {String(t('login.passwordResetRequest.submitButton'))}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};
